// "Novidades": abre ao clicar na versão do rodapé do menu (flag `crm.sistema.novidades`).
// Mostra, por dia de publicação, o que mudou para o clube — sem nada técnico e sem
// nome de clube (o texto é o mesmo para todos). Novidade atrás de flag só aparece
// para quem tem a flag ligada.
// Doc: docs/funcionalidades/novidades-da-versao.md
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sentry, toSentryError } from '@pps/observability';
import { useFeatureFlags } from '@pps/feature-flags';
import { dataParaExibir } from '@pps/datas-sp';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@pps/ui/components/ui/button';
import { NOVIDADES_POR_PAGINA, useNovidades } from '@/hooks/useNovidades';
import { agruparPorDia, novidadesVisiveis } from '@/lib/novidades';

interface Props {
  aberto: boolean;
  onFechar: () => void;
  versaoAtual: string;
}

export function NovidadesDialog({ aberto, onFechar, versaoAtual }: Props) {
  const { t, i18n } = useTranslation();
  const [limite, setLimite] = useState(NOVIDADES_POR_PAGINA);
  const consulta = useNovidades(limite, aberto);
  const { flags } = useFeatureFlags();

  useEffect(() => {
    if (!consulta.error) return;
    Sentry.captureException(toSentryError(consulta.error), { tags: { feature: 'novidades', action: 'carregar' } });
  }, [consulta.error]);

  const dias = useMemo(
    () => agruparPorDia(novidadesVisiveis(consulta.data?.itens ?? [], flags)),
    [consulta.data, flags],
  );
  const formatoDoDia = useMemo(
    // `dataParaExibir` devolve meio-dia UTC do dia civil: formatar em UTC mostra o mesmo
    // dia em qualquer fuso do aparelho (IR-TZ-006).
    () => new Intl.DateTimeFormat(i18n.language === 'pt' ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
    [i18n.language],
  );
  const temMais = (consulta.data?.total ?? 0) > limite;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="novidades-dialog">
        <DialogHeader>
          <DialogTitle>{t('sistema.novidades.titulo')}</DialogTitle>
          <DialogDescription>{t('sistema.novidades.descricao', { versao: versaoAtual })}</DialogDescription>
        </DialogHeader>

        {consulta.isLoading && <p className="text-sm text-muted-foreground">{t('sistema.novidades.carregando')}</p>}
        {consulta.isError && !consulta.data && (
          <p className="text-sm text-destructive" role="alert">{t('sistema.novidades.erro')}</p>
        )}
        {/* Vazio só quando o servidor não tem mais nada: a página pode vir toda com novidades
            de flags desligadas para este clube, e aí o caminho é "Ver mais antigas". */}
        {consulta.data && dias.length === 0 && !temMais && (
          <p className="text-sm text-muted-foreground">{t('sistema.novidades.vazio')}</p>
        )}

        <div className="space-y-5">
          {dias.map(({ dia, itens }) => (
            <section key={dia} aria-label={formatoDoDia.format(dataParaExibir(dia))}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {formatoDoDia.format(dataParaExibir(dia))}
              </h3>
              <ul className="space-y-3">
                {itens.map((n) => (
                  <li key={n.chave} className="rounded-md border p-3" data-testid="novidade">
                    <p className="text-sm font-medium">{n.titulo}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{n.texto}</p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {temMais && (
          <Button
            variant="outline"
            className="w-full"
            disabled={consulta.isFetching}
            onClick={() => setLimite((l) => l + NOVIDADES_POR_PAGINA)}
          >
            {t('sistema.novidades.verMais')}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
